'use client';

import { useCallback, useState } from 'react';
import { OptInManager } from '@fanduel/casino-swj-consumer';
import { useStore } from '@/store/store';
import { useAccountUm } from '@/hooks/use-account-um';
import { ddRumError } from '@/utils/datadog';
import { DD_USE_CASES } from '@/utils/datadog/types';
import { ddErrorSwjOptin } from '@/utils/datadog/site-wide-jackpot';
import { POPUP_ERROR_CODES, POPUPS } from '@/app/components/popup/constants';
import type { MultiplierValue } from '@/store/slices/site-wide-jackpot';
import type { SupportLink } from '@/types/layout/link-view-model';
import type { Account } from '@fanduel/cpe-um-bundle/account/library';

// Lazy-initialized singleton OptInManager instance.
let optInManagerInstance: OptInManager | null = null;

// Cached Account module. Loaded lazily on first session lookup so that SSR never
// evaluates @fanduel/account → @fanduel/core-ioc (which touches `window` at module init).
let accountModule: typeof Account | null = null;
const getAccount = async (): Promise<typeof Account> => {
    accountModule ??= (await import('@fanduel/cpe-um-bundle/account/library')).Account;
    return accountModule;
};

type ErrorType = {
    reason: string;
    message: string;
};

type OptInManagerOptions = {
    defaultJackpotId?: string;
    callback?: (
        arg:
            | number
            | {
                  [key: string]: boolean;
              }
    ) => void;
    errorCallback?: (error: ErrorType, multiplier?: number) => void;
};

/**
 * Gets or creates the singleton OptInManager instance.
 * IMPORTANT: OptInManager has its own singleton pattern, so the FIRST call
 * determines the configuration. We must ensure endpoint/origin are set on first creation.
 */
const getOptInManagerInstance = (
    endpoint?: string,
    origin?: 'lobby' | 'game_window',
    defaultJackpotId?: string,
    callbacks?: OptInManagerOptions
): OptInManager => {
    if (optInManagerInstance) {
        return optInManagerInstance;
    }

    const getSession = async () => {
        const Account = await getAccount();
        return Account?.getSessionAsync('Silent');
    };

    if (endpoint && origin) {
        optInManagerInstance = new OptInManager(getSession as () => Promise<{ token: string }>, {
            endpoint,
            origin,
            options: {
                defaultJackpotId,
                ...callbacks
            }
        });
        return optInManagerInstance;
    }

    // If we reach here, instance doesn't exist and we don't have proper config
    // This should never happen if configure() is called first
    throw new Error(
        'OptInManager cannot be created without endpoint and origin. ' +
            'Call optInManagerService.configure() first with proper parameters.'
    );
};

/**
 * Configures the OptInManager singleton with endpoint, origin, and jackpotId.
 * IMPORTANT: MUST be called before any other code accesses OptInManager.
 */
const configure = (
    endpoint: string,
    origin: 'lobby' | 'game_window',
    jackpotId: string,
    callbacks?: {
        callback?: (value: number | { [key: string]: boolean }) => void;
        errorCallback?: (error: ErrorType, multiplier?: number) => void;
    }
) => {
    const manager = getOptInManagerInstance(endpoint, origin, jackpotId, callbacks);

    // If instance already existed, we can only update callbacks
    if (optInManagerInstance && manager.currentJackpotId) {
        manager.overrideParams({
            ...callbacks
        });
    }
};

// Export getter for the singleton instance
export const optInManagerService = {
    getInstance: () => {
        if (!optInManagerInstance) {
            throw new Error('OptInManager not configured. OptInManagerService.configure must be called first.');
        }
        return optInManagerInstance;
    },
    configure
};

type ErrorPayload = {
    reason: string;
    message?: string;
};

type UseOptInManagerOptions = {
    support?: SupportLink;
};

export const useOptInManager = ({ support }: UseOptInManagerOptions = {}) => {
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const { multiplier: isOptedIn, setMultiplier } = useStore((s) => s.siteWideJackpot);
    const { queuePopup } = useStore((s) => s.popup);
    const { triggerLogin } = useAccountUm();

    const [isLoading, setIsLoading] = useState(false);

    /**
     * Handles errors from the OptInManager.
     */
    const handleError = useCallback(
        (error: ErrorPayload, multiplier?: number) => {
            ddRumError(ddErrorSwjOptin(error.reason, DD_USE_CASES.SITE_WIDE_JACKPOT, {}));

            queuePopup({
                type: POPUPS.SWJ_ERROR,
                params: {
                    errorCode: error.reason,
                    support,
                    multiplier
                }
            });
        },
        [queuePopup, support]
    );

    /**
     * Uses the opt-in manager methods to update the opt in status.
     * NOTE: optInManagerService.getInstance() should return a configured instance due to the earlier initialization in
     * the app, so configuration doesn't need to be performed here.
     */
    const updateOptInStatus = useCallback(
        async (multiplier?: number) => {
            if (multiplier === isOptedIn) {
                return;
            }

            setIsLoading(true);
            const manager = optInManagerService.getInstance();
            const isOptingIn = multiplier || !isOptedIn;
            const intendedMultiplier = isOptingIn ? multiplier : undefined;
            const errorCallbackWrapper = (error: ErrorType, errorMultiplier?: number) => {
                handleError(error, isOptingIn ? (errorMultiplier ?? intendedMultiplier) : undefined);
            };

            manager.overrideParams({
                callback: (v: number | { [key: string]: boolean }) => {
                    if (typeof v === 'number') {
                        setMultiplier(v as MultiplierValue);
                    }
                },
                errorCallback: errorCallbackWrapper
            });

            try {
                multiplier || !isOptedIn ? await manager.optIn(multiplier) : await manager.optOut();
            } catch (_error) {
                handleError(
                    { reason: isOptingIn ? POPUP_ERROR_CODES.OPT_IN_FAILED : POPUP_ERROR_CODES.OPT_OUT_FAILED },
                    intendedMultiplier
                );
            } finally {
                setIsLoading(false);
            }
        },
        [isOptedIn, setMultiplier, handleError]
    );

    /**
     * Handles the opt-in status change.
     * Redirects to login when user is logged out.
     */
    const handleOptIn = useCallback(
        async (multiplier?: number) => {
            if (!isLoggedIn) {
                triggerLogin();
                return;
            }

            await updateOptInStatus(multiplier);
        },
        [isLoggedIn, triggerLogin, updateOptInStatus]
    );

    return { isOptedIn, handleOptIn, isLoading };
};
